import type { ToggleOption } from '@/components'
import { FAIcon, ModuleWrapper, Toggles } from '@/components'
import { FullPageLoader } from '@/components_deprecated'
import { PageType } from '@/modules/common/consts'
import { Module } from '@/modules/companies'
import { endOfWeek, startOfWeek } from '@/utils/date-fns'
import type { useLeaderGroups } from '@/utils/hooks'
import {
  useAdmin,
  useFormQueryField,
  useFormWatch,
  useLeaderGroupsSuspense,
  useNodes,
  useOnErrorAuto,
  useParseQueryState,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { PERMISSIONS } from '@/utils/permissions'
import { isInvalidDate } from '@/utils/time'
import { useQuery } from '@apollo/client'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm as useFormOld, useTranslate } from '@ur-solutions/react-hooks'
import { addMonths, endOfMonth, setDate, startOfMonth } from 'date-fns'
import type { VariablesOf } from 'gql.tada'
import queryString from 'query-string'
import React, {
  Activity,
  Suspense,
  useEffect,
  useEffectEvent,
  useMemo,
  useState,
} from 'react'
import { BrowserView } from 'react-device-detect'
import type { UseFormReturn } from 'react-hook-form'
import { Controller, useForm } from 'react-hook-form'
import { useLocation } from 'react-router'
import { AbsenceRequests, AbsencesTable } from './components'
import type { AbsencesFiltersForm } from './components/absences-filters'
import {
  AbsencesFilters,
  AbsencesFiltersSchema,
} from './components/absences-filters'
import { AbsencesCalendar } from './components/AbsencesCalendar'
import { USER_ABSENCES_QUERY } from './queries'
import type { AbsenceFilterForm } from './types'
import type { AbsenceTypeNode } from './types.graphql'

interface InitialFilterParams {
  userId: string
  date: Date
}

function useInitialFilters(
  defaultLeaderGroup: ReturnType<typeof useLeaderGroups>['defaultLeaderGroup']
): AbsencesFiltersForm {
  const me = useUser()
  const admin = useAdmin()
  const { hasPermissions } = usePermissions()

  const parsed = useParseQueryState(AbsencesFiltersSchema, 'view')

  const dateStart = startOfMonth(addMonths(new Date(), -1))
  const dateEnd = endOfMonth(new Date())

  return {
    view: parsed.view ?? 'table',
    users: hasPermissions(PERMISSIONS.timesheets.view.othersabsence)
      ? []
      : [me.id],
    absenceType: '',
    requestStatus: '',
    dateRange: {
      from: dateStart,
      to: dateEnd,
    },
    month: new Date(),
    leaderGroup: !admin ? defaultLeaderGroup?.id ?? '' : '',
    salaryMode: false,
  }
}

function useAbsencesQueryVariables(
  form: UseFormReturn<AbsencesFiltersForm>
): VariablesOf<typeof USER_ABSENCES_QUERY> {
  const view = useFormWatch(form, 'view')

  const users = useFormWatch(form, 'users')
  const absenceType = useFormWatch(form, 'absenceType') || undefined
  const requestStatus = useFormWatch(form, 'requestStatus') || undefined
  const leaderGroup = useFormWatch(form, 'leaderGroup') || undefined

  const { from, to } = useFormWatch(form, 'dateRange')
  const month = useFormWatch(form, 'month')
  let datetimeStartGte: string | undefined = from?.toISOString()
  let datetimeStartLte: string | undefined = to?.toISOString()

  if (view === 'calendar') {
    datetimeStartGte = startOfWeek(startOfMonth(month)).toISOString()
    datetimeStartLte = endOfWeek(endOfMonth(month)).toISOString()
  }

  return {
    users,
    absenceType,
    requestStatus,
    leaderGroup,
    datetimeStartGte,
    datetimeStartLte,
  }
}

interface AbsencesProps {}

const AbsencesInner: React.FC<AbsencesProps> = () => {
  const translations = useTranslate({
    absences: 'common.absences',
  })

  const [selectedAbsenceType, setSelectedAbsenceType] = useState<Pick<
    AbsenceTypeNode,
    'id' | 'name'
  > | null>(null)

  const me = useUser()
  const isAdmin = useAdmin()
  const location = useLocation()
  const { hasPermissions } = usePermissions()

  const canManageRequests = hasPermissions(
    PERMISSIONS.timesheets.change.othersabsence
  )

  const initialFilter = useMemo<InitialFilterParams>(() => {
    const search = queryString.parse(location.search)
    const date = (search.date as string) || null
    const userId = (search.user as string) || null
    return {
      date: date ? new Date(date) : new Date(),
      userId: hasPermissions(PERMISSIONS.timesheets.change.absence)
        ? userId ?? me.id
        : me.id,
    }
  }, [hasPermissions, location.search, me.id])

  const [initialLoaded, setInitialLoaded] = useState(false)

  const leaderGroups = useLeaderGroupsSuspense()

  const form = useForm<AbsencesFiltersForm>({
    resolver: zodResolver(AbsencesFiltersSchema),
    defaultValues: useInitialFilters(leaderGroups.defaultLeaderGroup),
  })

  useFormQueryField(form, 'view')

  const {
    formValues: filter,
    updateForm,
    updateInitialValues,
  } = useFormOld<AbsenceFilterForm>({
    values: {
      users: [],
      user: undefined,
      month: initialFilter.date,
      date: [initialFilter.date, initialFilter.date],
      orderBy: 'start',
      isDate: false,
      isDateStart: false,
      allUsers: false,
      requestStatus: undefined,
      view: 'table',
      salaryMode: false,
      leaderGroup: null,
      leaderGroupUsers: [],
    },
    config: {
      storage: {
        storeFormState: true,
        storeFormStateName: 'absence-controls',
        retrieveFormStateOnMount: true,
        transformValues: {
          month: val =>
            typeof val === 'string'
              ? isInvalidDate(new Date(val), new Date())
              : undefined,
          salaryMode: value => (typeof value === 'boolean' ? value : false),
        },
      },
    },
  })

  const user = useUser(filter.user, Module.TIMESHEETS)

  const filterView = useFormWatch(form, 'view')
  const filterSalaryMode = useFormWatch(form, 'salaryMode')
  const filterMonth = useFormWatch(form, 'month')

  const { datetimeEndGte, datetimeStartLt } = (() => {
    if (filterSalaryMode && user.salaryWorkTime && filterView === 'table') {
      const salaryStart = user.salaryWorkTime?.salaryDateStart ?? 1
      const salaryEnd = user.salaryWorkTime?.salaryDateEnd ?? 1

      const forward = 31 - salaryStart > 15

      // What salary period is connected to which month is based on the salary start date.
      if (forward) {
        const datetimeStartLt = setDate(filterMonth, salaryStart)
        const datetimeEndGte =
          salaryStart === 1
            ? endOfMonth(filterMonth)
            : setDate(addMonths(filterMonth, 1), salaryEnd)

        return { datetimeStartLt, datetimeEndGte }
      }

      const datetimeStartLt = setDate(addMonths(filterMonth, -1), salaryStart)
      const datetimeEndGte = setDate(filterMonth, salaryEnd)

      return { datetimeStartLt, datetimeEndGte }
    } else {
      const startOfMonthDate = startOfMonth(filterMonth)
      const endOfMonthDate = addMonths(startOfMonthDate, 1)

      const datetimeStartLt =
        filterView === 'table'
          ? startOfMonthDate
          : startOfWeek(startOfMonthDate)
      const datetimeEndGte =
        filterView === 'table' ? endOfMonthDate : endOfWeek(endOfMonthDate)

      return {
        datetimeStartLt,
        datetimeEndGte,
      }
    }
  })()

  const onErrorAuto = useOnErrorAuto()
  const queryVariables = useAbsencesQueryVariables(form)

  const { data, loading } = useQuery(USER_ABSENCES_QUERY, {
    skip: filterView === 'requests',
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: queryVariables,
    onError: onErrorAuto(),
  })

  const absences = useNodes(data?.allAbsences, absence => {
    if (!filter.leaderGroupUsers.length) return true
    return filter.leaderGroupUsers.includes(absence.user.id)
  })

  const viewToggleOptions: ToggleOption<AbsencesFiltersForm['view']>[] = [
    {
      value: 'requests',
      label: <FAIcon icon="memo-circle-check" />,
      hide: !canManageRequests,
      badge: data?.pendingAbsenceCount || null,
    },
    {
      value: 'calendar',
      label: <FAIcon icon="calendar-alt" />,
    },
    {
      value: 'table',
      label: <FAIcon icon="list" />,
    },
  ]

  const handleSetInitialLoaded = useEffectEvent(() => {
    setInitialLoaded(true)
  })

  useEffect(() => {
    if (initialFilter.date !== filter.date[0] && !initialLoaded) {
      updateForm({ date: [initialFilter.date, initialFilter.date] })
      handleSetInitialLoaded()
    }
  }, [filter.date, initialFilter.date, initialLoaded, updateForm])

  // TODO: Add to multiple pages if logic is suitable
  useEffect(() => {
    if (
      leaderGroups.defaultLeaderGroup &&
      !filter.leaderGroup &&
      leaderGroups.myLeaderGroups.length === 1 &&
      !isAdmin
    ) {
      updateForm({
        leaderGroup: leaderGroups.defaultLeaderGroup.id,
        leaderGroupUsers: leaderGroups.defaultLeaderGroup.users.map(
          user => user.id
        ),
      })
      updateInitialValues({ leaderGroup: leaderGroups.defaultLeaderGroup.id })
    }
  }, [
    filter.leaderGroup,
    isAdmin,
    leaderGroups.defaultLeaderGroup,
    leaderGroups.myLeaderGroups.length,
    updateForm,
    updateInitialValues,
  ])

  return (
    <ModuleWrapper
      title={translations.absences}
      page={PageType.ABSENCES}
      headerContent={
        <BrowserView>
          <div className="flex gap-4">
            <Controller
              control={form.control}
              name="view"
              render={({ field }) => (
                <Toggles
                  {...field}
                  options={viewToggleOptions}
                  variant="white"
                  size="lg"
                />
              )}
            />
          </div>
        </BrowserView>
      }
    >
      <AbsencesFilters form={form} leaderGroups={leaderGroups} />

      <Activity mode={filterView === 'table' ? 'visible' : 'hidden'}>
        <AbsencesTable
          absences={absences}
          loading={loading}
          selectedAbsenceType={selectedAbsenceType}
          onSelectAbsenceType={absenceType => {
            setSelectedAbsenceType(absenceType)
          }}
        />
      </Activity>

      <Activity mode={filterView === 'calendar' ? 'visible' : 'hidden'}>
        <AbsencesCalendar
          startDate={datetimeStartLt}
          endDate={datetimeEndGte}
          absences={absences}
        />
      </Activity>

      <Activity mode={filterView === 'requests' ? 'visible' : 'hidden'}>
        <AbsenceRequests filter={form} />
      </Activity>
    </ModuleWrapper>
  )
}

export function Absences() {
  return (
    <Suspense fallback={<FullPageLoader />}>
      <AbsencesInner />
    </Suspense>
  )
}
